import { arrayMove } from '@dnd-kit/sortable';
import { useState } from 'react';
import { v4 as uuid } from 'uuid';
import { CatalogSectionCount } from '../../../catalog.types';
import {
  BatchSectionEntryPayload,
  CatalogSectionStagingState,
  RenameStagedSectionParams,
  ReorderStagedSectionParams,
  StagedSection,
  UseCatalogSectionStagingParams,
} from './useCatalogSectionStaging.types';

const toStagedSections = (persisted: CatalogSectionCount[]): StagedSection[] =>
  persisted.map((section) => ({
    key: section.catalogSectionId,
    id: section.catalogSectionId,
    name: section.catalogSectionName,
    isDefault: section.isDefault,
  }));

export const useCatalogSectionStaging = ({ persisted }: UseCatalogSectionStagingParams) => {
  const [state, setState] = useState<CatalogSectionStagingState>(() => ({
    entries: toStagedSections(persisted),
    editingKey: null,
  }));

  const reorder = ({ fromIndex, toIndex }: ReorderStagedSectionParams) => {
    setState((current) => ({
      ...current,
      entries: arrayMove(current.entries, fromIndex, toIndex),
    }));
  };

  const rename = ({ key, name }: RenameStagedSectionParams) => {
    setState((current) => ({
      ...current,
      entries: current.entries.map((entry) => (entry.key === key ? { ...entry, name } : entry)),
    }));
  };

  const remove = (key: string) => {
    setState((current) => ({
      entries: current.entries.filter((entry) => entry.key !== key),
      editingKey: current.editingKey === key ? null : current.editingKey,
    }));
  };

  const create = (name: string) => {
    const key = uuid();
    setState((current) => ({
      entries: [...current.entries, { key, name, isDefault: false }],
      editingKey: current.editingKey,
    }));
    return key;
  };

  const startEdit = (key: string) => {
    setState((current) =>
      current.editingKey === null ? { ...current, editingKey: key } : current,
    );
  };

  const cancelEdit = () => setState((current) => ({ ...current, editingKey: null }));

  return {
    entries: state.entries,
    editingKey: state.editingKey,
    reorder,
    rename,
    remove,
    create,
    startEdit,
    cancelEdit,
  };
};

export const toBatchPayload = (entries: StagedSection[]): BatchSectionEntryPayload[] =>
  entries.map((entry) => ({ id: entry.id, name: entry.name }));

export const isStagingDirty = (entries: StagedSection[], persisted: CatalogSectionCount[]) => {
  if (entries.length !== persisted.length) {
    return true;
  }

  return entries.some(
    (entry, index) =>
      entry.id !== persisted[index].catalogSectionId ||
      entry.name !== persisted[index].catalogSectionName,
  );
};

export const findDeletedSectionsWithItems = (
  entries: StagedSection[],
  persisted: CatalogSectionCount[],
) => {
  const stagedIds = new Set(entries.map((entry) => entry.id));
  return persisted.filter(
    (section) => !stagedIds.has(section.catalogSectionId) && section.itemsCount > 0,
  );
};
